import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Activity, Plus } from '../../../components/icons';
import { fetchCheckIns, createCheckIn, type CheckIn } from '../../../lib/progress';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

function avg(arr: number[]) {
  if (!arr.length) return 0;
  return Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
}

function ScoreBar({ value, color }: { value: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-cream-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${(value / 10) * 100}%` }} />
      </div>
      <span className="text-sm font-semibold text-chocolate-700 w-5 text-right">{value}</span>
    </div>
  );
}

export function CheckInsViewTR() {
  const { user } = useAuth();
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ energy_level: 5, mood_level: 5, focus_level: 5, notes: '' });

  useEffect(() => {
    if (!user) return;
    fetchCheckIns(user.id).then(setCheckIns).finally(() => setIsLoading(false));
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const created = await createCheckIn(user.id, { ...form, checked_in_at: new Date().toISOString() });
    if (created) setCheckIns(prev => [created, ...prev]);
    setShowForm(false);
    setForm({ energy_level: 5, mood_level: 5, focus_level: 5, notes: '' });
  };

  const recent7 = checkIns.slice(0, 7);
  const avgEnergy = avg(recent7.map(c => c.energy_level));
  const avgMood = avg(recent7.map(c => c.mood_level));
  const avgFocus = avg(recent7.map(c => c.focus_level));

  const todayCheckedIn = checkIns.length > 0 && new Date(checkIns[0].checked_in_at).toDateString() === new Date().toDateString();

  if (isLoading) return <LoadingSpinner message="Check-in'ler yükleniyor..." />;

  return (
    <div className="p-6 md:p-8 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Check-in</h1>
            <p className="text-sm text-chocolate-500">{checkIns.length} toplam check-in</p>
          </div>
        </div>
        {!todayCheckedIn && (
          <button onClick={() => setShowForm(true)} className="freud-btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Bugünkü Check-in
          </button>
        )}
        {todayCheckedIn && (
          <span className="text-sm text-sage-600 font-medium bg-sage-50 px-3 py-1.5 rounded-lg">Bugün check-in yapıldı</span>
        )}
      </div>

      {checkIns.length > 0 && (
        <div className="freud-card-elevated p-6 mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-chocolate-400 mb-4">7 Günlük Ortalama</p>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-chocolate-600">Enerji</span>
              </div>
              <ScoreBar value={avgEnergy} color="bg-amber-400" />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-chocolate-600">Ruh Hali</span>
              </div>
              <ScoreBar value={avgMood} color="bg-coral-400" />
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-chocolate-600">Odak</span>
              </div>
              <ScoreBar value={avgFocus} color="bg-sage-400" />
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">Bugünkü Check-in</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            {[
              { key: 'energy_level', label: 'Enerji', color: 'text-amber-500' },
              { key: 'mood_level', label: 'Ruh Hali', color: 'text-coral-500' },
              { key: 'focus_level', label: 'Odak', color: 'text-sage-500' },
            ].map(({ key, label, color }) => (
              <div key={key}>
                <div className="flex justify-between mb-2">
                  <label className="freud-label mb-0">{label}</label>
                  <span className={`text-lg font-bold ${color}`}>{form[key as keyof typeof form]}</span>
                </div>
                <input
                  type="range" min={1} max={10}
                  value={form[key as keyof typeof form] as number}
                  onChange={e => setForm(p => ({ ...p, [key]: Number(e.target.value) }))}
                  className="w-full accent-sage-500"
                />
                <div className="flex justify-between text-[10px] text-chocolate-400 mt-1">
                  <span>1 — Düşük</span>
                  <span>10 — Yüksek</span>
                </div>
              </div>
            ))}
            <div>
              <label className="freud-label">Notlar</label>
              <textarea className="freud-input" rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Aklınızdaki bir şey var mı?" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">Check-in Gönder</button>
              <button type="button" onClick={() => setShowForm(false)} className="freud-btn-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {checkIns.length === 0 && !showForm ? (
        <div className="freud-card-elevated p-12 text-center">
          <Activity className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">Henüz check-in yok. Günlük refahınızı takip etmeye başlayın.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {checkIns.map(ci => (
            <div key={ci.id} className="freud-card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-chocolate-700">{new Date(ci.checked_in_at).toLocaleDateString('tr-TR', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                <div className="flex gap-3 text-xs text-chocolate-500">
                  <span>E: <strong className="text-amber-500">{ci.energy_level}</strong></span>
                  <span>R: <strong className="text-coral-500">{ci.mood_level}</strong></span>
                  <span>O: <strong className="text-sage-600">{ci.focus_level}</strong></span>
                </div>
              </div>
              {ci.notes && <p className="text-sm text-chocolate-500 italic">"{ci.notes}"</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
